<template>
  <div class="pallet-detail f-c" v-loading="loadingPalletInfo">
    <pallet-detail-stats
      :pallet="palletDetail"
      :riiContainerNo="containerNo"
      :riiOrderNo="riiOrderNo"
    />
    <div class="f-r-vc detail-page-basic-info" style="gap: 20px">
      <span class="entity-no">{{ palletNo }}</span>
      <el-tag :type="palletStatusTag" effect="dark" class="basic-info-tag">
        {{ palletStatus }}
      </el-tag>
      <el-tag type="danger" effect="dark" class="basic-info-tag" v-if="isHold">
        <svg-icon
          icon-class="lock"
          class="svg-lock svg-white-color"
          style="font-size: 30px"
        />
        <span>Hold</span>
      </el-tag>
    </div>
    <div class="features f-r-vc common-card-margin">
      <el-button
        type="success"
        class="feature-secondary"
        @click="goToOrderDetail(orderNo)"
      >
        <svg-icon
          icon-class="info"
          class="svg-white-color"
          style="font-size: 18px"
        />
        {{ riiOrderNo }}
      </el-button>
      <el-button
        type="success"
        class="feature-secondary"
        v-if="unloadingJobNo"
        @click="goToJobDetail(unloadingJobNo)"
      >
        <svg-icon
          icon-class="info"
          class="svg-white-color"
          style="font-size: 18px"
        />
        Unloading {{ unloadingJobNo }}
      </el-button>
      <el-button
        type="success"
        class="feature-secondary"
        :disabled="palletStatus === PalletDeleted"
        @click="viewPalletLabel"
      >
        <svg-icon icon-class="labels" class="svg-white-color" />Label
      </el-button>
      <el-button type="success" class="feature-secondary" @click="openQrCode">
        <svg-icon
          icon-class="info"
          class="svg-white-color"
          style="font-size: 18px"
        />QR Code
      </el-button>
      <el-button
        type="warning"
        class="feature-secondary"
        v-if="palletStatus !== PalletDeleted"
        @click="openModifyPallet"
      >
        <svg-icon
          icon-class="pencil"
          class="svg-white-color"
          style="font-size: 16px"
        />
        Modify Pallet
      </el-button>
      <el-button
        type="danger"
        class="feature-secondary"
        v-if="palletStatus !== PalletDeleted"
        @click="openPalletDelete"
      >
        <svg-icon
          icon-class="trash"
          class="svg-white-color"
          style="font-size: 14px"
        />
        Delete Pallet
      </el-button>
    </div>
    <pallet-info :pallet-id="palletId" :pallet="palletDetail" />
    <order-info
      :order-id="orderId"
      :order="palletOrder"
      :editable="orderFormEditable"
      :check-detail="checkOrderDetail"
    />
    <container-info
      v-if="[DirectOrder, UnloadingOrder].includes(orderType)"
      :order-status="orderStatus"
      :container-id="containerId"
      :container="container"
      :enableEdit="false"
    />
    <address-info :addressId="addressId" />
    <div class="tabs">
      <el-tabs v-model="selectedTab">
        <el-tab-pane label="Goods" name="GoodsListTab">
          <packing-list :queryParams="goodsQuery" :show-actions="false" />
        </el-tab-pane>
        <el-tab-pane label="Jobs" name="PalletJobsTab">
          <job-list :jobQuery="{ ids: jobIds }" v-if="jobIds.length > 0" />
        </el-tab-pane>
        <el-tab-pane label="Notes" name="NotesTab">
          <el-button
            size="small"
            plain
            type="primary"
            @click="newPalletNote"
            :disabled="palletStatus === PalletDeleted"
          >
            Add New Note
          </el-button>
          <note-list :notes="notes" :change-cb="modifyNotes" />
        </el-tab-pane>
        <el-tab-pane label="Picture" name="Picture">
          <pallet-picture :no="palletNo" />
        </el-tab-pane>
      </el-tabs>
    </div>
    <goods-edit ref="goodsEdit" />
    <common-dialog ref="qrCode" class="qr-code">
      <template v-slot:content>
        <img :src="src" />
      </template>
    </common-dialog>
    <confirm
      ref="deleteConfirm"
      :title="'Delete Pallet ' + palletNo"
      :confirmCb="confirmDelete"
      :loading="deleteLoading"
    >
      <template v-slot:content>
        <div class="f-c confirm-delete-hint">
          <span>
            1. Pallet belongs to Order <strong>{{ riiOrderNo }}.</strong>
          </span>
          <span v-if="deleteWarning">
            2. Pallet appeared in
            <strong style="font-size: 24px">{{
              deleteWarning?.shipmentNo ?? 'No Shipment'
            }}</strong>
          </span>
          <span>3. Pallet deleted cannot be restored!</span>
          <span></span>
        </div>
      </template>
    </confirm>
    <note-edit ref="newNote" />
    <pallet-modify ref="palletModify"></pallet-modify>
  </div>
</template>
<script>
import { FormModes } from '@/core/configs/types'
import { ContainerInfoWrapUp } from '@/core/models/container'
// models
import {
  PalletDelivered,
  PalletOffStack,
  PalletOnStack,
  PalletOnStage,
  PalletShipping,
  PalletUpcoming,
  PalletDeleted
} from '@/core/models/pallet'
import { HOLD, UNHOLD } from '@/core/models/constant'
import { DirectOrder, UnloadingOrder } from '@/core/models/order'
// apis
import {
  deletePallet,
  detailPallet,
  labelPallet,
  modifyPallet
} from '@/api/pallet'
// mixin
import { pageJumps } from '@/core/mixin/pages'
import { mapGetters } from 'vuex'
// components
import PalletInfo from '@/components/pallet/PalletInfo.vue'
import OrderInfo from '@/components/order/OrderInfo.vue'
import PackingList from '@/components/goods/PackingList.vue'
import GoodsEdit from '@/components/goods/GoodsEdit.vue'
import JobList from '@/components/jobs/JobList.vue'
import NoteList from '@/components/note/NoteList.vue'
import CommonDialog from '@/components/common/CommonDialog.vue'
import PalletDetailStats from '@/components/pallet/PalletDetailStats.vue'
import ContainerInfo from '@/components/container/ContainerInfo.vue'
import AddressInfo from '@/components/address/AddressInfo.vue'
import Confirm from '@/components/common/Confirm.vue'
import NoteEdit from '@/components/note/NoteNew.vue'
import PalletModify from '@/components/pallet/PalletModify.vue'
import PalletPicture from '@/components/pallet/PalletPicture.vue'
// msg
import PagesMessage from '@/core/message/pages'
import { detailOrder } from '@/api/order'
import { viewPdf } from '@/core/utils'

const QRCode = require('qrcode')

export default {
  name: 'PalletDetail',
  mixins: [pageJumps],
  components: {
    PalletInfo,
    OrderInfo,
    PackingList,
    GoodsEdit,
    JobList,
    NoteList,
    CommonDialog,
    PalletDetailStats,
    ContainerInfo,
    AddressInfo,
    Confirm,
    NoteEdit,
    PalletModify,
    PalletPicture
  },
  data() {
    return {
      HOLD,
      UNHOLD,
      FormModes,
      DirectOrder,
      UnloadingOrder,
      PalletDeleted,
      loadingPalletInfo: false,
      palletDetail: {},
      orderFormEditable: false,
      jobFormEditable: false,
      checkOrderDetail: true,
      src: '',
      palletOrder: null,
      deleteWarning: null,
      deleteLoading: false
    }
  },
  computed: {
    ...mapGetters(['username', 'nickname']),
    selectedTab: {
      get() {
        return this.$route.query.tab
      },
      set(value) {
        this.goToPalletDetail(this.palletNo, { tab: value })
      }
    },
    palletId() {
      return this.palletDetail?.id ?? ''
    },
    palletNo() {
      return this.$route.params.no
    },
    palletStatus() {
      return this.palletDetail?.status
    },
    palletStatusTag() {
      switch (this.palletStatus) {
        case PalletUpcoming:
          return 'warning'
        case PalletDeleted:
          return 'danger'
        default:
          return 'primary'
      }
    },
    palletInfoOps() {
      return []
    },
    orderId() {
      return this.palletDetail?.orderId ?? ''
    },
    orderNo() {
      return this.palletOrder?.no ?? ''
    },
    orderType() {
      return this.palletOrder?.type ?? ''
    },
    orderStatus() {
      return this.palletOrder?.status ?? ''
    },
    riiOrderNo() {
      return this.palletOrder?.riiOrderNo ?? ''
    },
    containerNo() {
      return this.palletOrder?.container?.no ?? ''
    },
    containerId() {
      return this.palletOrder?.containerId ?? ''
    },
    unloadingJobNo() {
      return this.palletOrder?.unloadingJobNo ?? ''
    },
    orderInfoOps() {
      return []
    },
    container() {
      return this.palletOrder?.container ?? {}
    },
    containerDetail() {
      return ContainerInfoWrapUp(this.containerInfos?.[0] ?? {})
    },
    containerInfoOps() {
      return []
    },
    goodsIds() {
      return this.palletDetail?.goodsIds ?? []
    },
    goodsQuery() {
      return this.goodsIds.length > 0 ? { ids: this.goodsIds } : null
    },
    jobIds() {
      return this.palletDetail?.jobIds ?? []
    },
    notes() {
      return this.palletDetail?.notes ?? []
    },
    isHold() {
      return this.palletDetail.hold === HOLD
    },
    addressId() {
      return this.palletDetail?.addressId ?? ''
    }
  },
  mounted() {
    this.getPalletDetail()
    PagesMessage.on(
      PagesMessage.eventMap.REFRESH_PALLET_DETAIL,
      this.getPalletDetail
    )
  },
  methods: {
    async getPalletDetail() {
      this.loadingPalletInfo = true
      const palletDetail = await detailPallet(this.palletNo)
      this.loadingPalletInfo = false
      await this.getPalletOrder(palletDetail.orderId)
      this.palletDetail = palletDetail
    },
    async getPalletOrder(orderId) {
      const res = await detailOrder(orderId)
      if (res) {
        this.palletOrder = res
      }
    },
    modifyNotes(notes) {
      modifyPallet(this.palletId, { notes }).then(pallet => {
        this.palletDetail.notes = notes
      })
    },
    async getSrc() {
      const src = await QRCode.toDataURL(
        JSON.stringify({
          type: 'Pallet',
          id: this.palletDetail.id,
          no: this.palletDetail.no
        })
      )
      this.src = src
    },
    openQrCode() {
      this.getSrc().then(e => this.$refs.qrCode.open())
    },
    async viewPalletLabel() {
      const blob = await labelPallet(this.palletNo)
      viewPdf(blob)
    },
    confirmDelete(done, error, confirmed = true) {
      this.deleteLoading = true
      deletePallet(this.palletId, confirmed, this.username)
        .then(res => {
          this.deleteLoading = false
          if (confirmed) {
            this.getPalletDetail()
            done()
          } else {
            this.deleteWarning = res
          }
        })
        .catch(err => {
          if (error) {
            error('failed to check pallet, cannot delete it, ' + err.toString())
          }
        })
        .finally(() => {})
    },
    openPalletDelete() {
      this.$refs.deleteConfirm.open()
      this.confirmDelete(null, null, false)
    },
    getPalletStatusStyle() {
      const status = this.palletDetail?.status ?? PalletUpcoming
      switch (status) {
        case PalletUpcoming:
          return { 'background-color': 'red !important' }
        case PalletOnStack:
          return { 'background-color': 'red' }
        case PalletOffStack:
          return { 'background-color': 'red' }
        case PalletOnStage:
          return { 'background-color': 'red' }
        case PalletDelivered:
          return { 'background-color': 'red' }
        case PalletShipping:
          return { 'background-color': 'red' }
      }
    },
    newPalletNote() {
      const saveNote = (newNote, done, error) => {
        modifyPallet(this.palletId, { notes: [...this.notes, newNote] })
          .then(() => {
            done()
            this.$message({
              type: 'success',
              message: 'Successfully add a new pallet note!'
            })
            PagesMessage.emit(PagesMessage.eventMap.REFRESH_PALLET_DETAIL)
          })
          .catch(err => {
            error(err)
          })
      }
      this.$refs.newNote.open(saveNote)
    },
    openModifyPallet() {
      this.$refs.palletModify.open(this.palletDetail, () => {
        PagesMessage.emit(PagesMessage.eventMap.REFRESH_PALLET_DETAIL)
      })
    }
  }
}
</script>
<style scoped lang="scss">
@import '@/assets/styles/common.scss';

.pallet-detail {
  @extend .f-c;
  //
}

.tabs {
  border-radius: $home-card-border-radius;
  padding: $home-card-padding-y $home-card-padding-x;
  background-color: white;

  @extend .shadow-level-three;
  @extend .common-card-margin;
}

::v-deep .qr-code {
  .el-dialog__body {
    @extend .f-r-c-c;
  }
}

.detail-page-basic-info {
  @extend .global-margin;
}

.confirm-delete-hint {
  border: 1px solid #dcdfe6;
  font-size: 16px;
  border-radius: 3px;
  margin-bottom: 10px;
  gap: 5px;
  padding: 10px;
  color: rgba($deep-danger, 0.6);
  word-break: break-word;
}
</style>
